/**
 * PermissionRoute Component
 * 
 * A route wrapper that checks for specific permissions before rendering children.
 * Falls back to admin check if permissions are not granted.
 */

import React from 'react'
import { connect, useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'

// Permission constants (keep in sync with backend Permissions model)
export const PERMISSIONS = {
  PIPELINE_VIEW: 'pipeline:view',
  PIPELINE_VIEW_ALL: 'pipeline:view_all',
  PIPELINE_EDIT: 'pipeline:edit',
  PIPELINE_ASSIGN: 'pipeline:assign',
  PIPELINE_ADMIN: 'pipeline:admin',
  PIPELINE_HUBSPOT: 'pipeline:hubspot',
  PIPELINE_DEBUG: 'pipeline:debug',
  PIPELINE_ADVISOR_READ: 'pipeline:advisor_read',
  PIPELINE_ADVISOR_GENERATE: 'pipeline:advisor_generate',
  REPORTS_VIEW: 'reports:view',
  REPORTS_EXPORT: 'reports:export',
  REPORTS_CREATE: 'reports:create',
  REPORTS_ADMIN: 'reports:admin',
  CUSTOMERS_VIEW: 'customers:view',
  CUSTOMERS_EDIT: 'customers:edit',
  CUSTOMERS_MIMIC: 'customers:mimic',
  CUSTOMERS_BILLING: 'customers:billing',
  CUSTOMERS_ADMIN: 'customers:admin',
  TEAM_VIEW: 'team:view',
  TEAM_MANAGE: 'team:manage',
  TEAM_PERMISSIONS: 'team:permissions',
  SYSTEM_SETTINGS: 'system:settings',
  SYSTEM_INTEGRATIONS: 'system:integrations',
  SYSTEM_AUDIT: 'system:audit',
  SYSTEM_ADMIN: 'system:admin',
  // Risk visibility (underwriter / insurer / internal personas only).
  // Ordinary merchant/client shipping users must NOT have these.
  RISK_VIEW: 'risk:view',
  RISK_ORDER_DETAIL: 'risk:order_detail',
  RISK_MERCHANT_PROFILE: 'risk:merchant_profile',
  // View the outbound risk-tag audit (what PHRISK we sent ShipSurance).
  RISK_AUDIT: 'risk:audit',
  // Shipping capabilities. Split out from the legacy single 'shipping'
  // team page permission. See
  // docs/shipping-audit/09-shipping-permission-split.md for the
  // backward-compat strategy (legacy users with no shipping:* row are
  // grandfathered into all four).
  SHIPPING_VIEW: 'shipping:view',
  SHIPPING_PURCHASE: 'shipping:purchase',
  SHIPPING_CLAIMS: 'shipping:claims',
  SHIPPING_SETTINGS: 'shipping:settings',
  SHIPPING_SANDBOX: 'shipping:sandbox',
} as const

interface PermissionRouteProps {
  admin: boolean
  permissions: string[]
  requiredPermission?: string
  requiredAnyPermission?: string[]
  children: React.ReactNode
  fallbackPath?: string
}

const PermissionRoute: React.FC<PermissionRouteProps> = ({
  admin,
  permissions,
  requiredPermission,
  requiredAnyPermission,
  children,
  fallbackPath = '/dashboard',
}) => {
  // Admins have all permissions
  if (admin) {
    return <>{children}</>
  }

  // Check single permission
  if (requiredPermission) {
    const hasPermission = permissions.includes(requiredPermission)
    if (!hasPermission) {
      return <Navigate to={fallbackPath} replace />
    }
    return <>{children}</>
  }

  // Check any of multiple permissions
  if (requiredAnyPermission && requiredAnyPermission.length > 0) {
    const hasAnyPermission = requiredAnyPermission.some(p => permissions.includes(p))
    if (!hasAnyPermission) {
      return <Navigate to={fallbackPath} replace />
    }
    return <>{children}</>
  }

  // No permissions specified, deny access
  return <Navigate to={fallbackPath} replace />
}

const mapStateToProps = (store: any) => ({
  admin: !!store.root.admin,
  permissions: store.root.permissions || [],
})

export default connect(mapStateToProps)(PermissionRoute)

/**
 * Hook to check a single permission in functional components.
 * Admins are always granted access.
 */
export const useHasPermission = (permission: string): boolean => {
  const admin = useSelector((state: any) => !!state.root?.admin)
  const perms: string[] = useSelector((state: any) => state.root?.permissions || [])
  if (admin) return true
  return perms.includes(permission)
}

/**
 * Hook to check if user has any of the specified permissions.
 * Admins are always granted access.
 */
export const useHasAnyPermission = (permissions: string[]): boolean => {
  const admin = useSelector((state: any) => !!state.root?.admin)
  const perms: string[] = useSelector((state: any) => state.root?.permissions || [])
  if (admin) return true
  return permissions.some(p => perms.includes(p))
}

/**
 * Hook to check if current user is an admin.
 */
export const useIsAdmin = (): boolean => {
  return useSelector((state: any) => !!state.root?.admin)
}
